'use client';

import { memo, useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import styles from "./FaqWithoutCategory.module.scss"
import cn from "clsx";
import ArrowSVG from '@/SvgIcons/ArrowDown';

const FaqAnswer = ({ faq }: { faq: any }) => {
  if (faq.Type === "SPECIALIZATION") {
    return (
      <ul
        className={cn("!text-[#1A1A1A]-600", styles.faqAns)}
        dangerouslySetInnerHTML={{ __html: faq.Answer }}
      />
    );
  }
  if (faq.Answer) {
    return (
      <p className={cn("!text-[#1A1A1A]-600", styles.faqAns)}>
        {faq.Answer}
      </p>
    );
  }
  return (
    <div
      className={cn("!text-[#1A1A1A]-600", styles.faqAns, styles.faqAnsCk)}
      dangerouslySetInnerHTML={{ __html: faq.AnswerCK }}
    />
  );
};

interface FaqItemProps {
  faq: any;
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
}

// Memoized so a click only re-renders the item being opened and the one being
// closed, instead of the entire FAQ list (keeps INP low).
const FaqItem = memo(({ faq, index, isOpen, onToggle }: FaqItemProps) => (
  <div
    className={cn(styles.faqItem, {
      [styles.faqItemOpen]: isOpen,
    })}
  >
    <div className={styles.faqQuesHeading}>
      <button
        type="button"
        className={cn(
          "flex justify-between items-center cursor-pointer py-1 w-full text-left",
          styles.faqQues
        )}
        onClick={() => onToggle(index)}
        aria-expanded={isOpen}
      >
       {faq.Question? <h3 className={cn("font-medium", styles.faqQue, {
          [styles.activeQuestion] : isOpen
        })}>
          {faq.Question}
        </h3>:<h3 className={cn("font-medium", styles.faqQue, {
          [styles.activeQuestion] : isOpen
        })} dangerouslySetInnerHTML={{__html : faq.QuestionCK}}></h3>}
        <span className={styles.icon}>
          <ArrowSVG
          id = {`faq-icon-${index}`}
            rotate={isOpen ? 180 : 0}
            width={16}
            height={16}
            isActive={isOpen}
            color="blue"
          />
        </span>
      </button>
    </div>
    <div
      className={cn(styles.answerCollapse, {
        [styles.answerOpen]: isOpen,
      })}
    >
      <div className={styles.answerCollapseInner}>
        <FaqAnswer faq={faq} />
      </div>
    </div>
  </div>
));
FaqItem.displayName = "FaqItem";

const FaqWithCategoryProgram = ({ QuestionAnswer,isHome=false}) => {
  const { tabs, categorizedFAQs } = useMemo(() => {
    const tabs = [];
    const categorizedFAQs = QuestionAnswer.reduce((acc, item) => {
      if(!item?.enabled) return acc;
      const type = item.Type?item.Type.replace(/_/g, " ").trim() : "OTHERS" ;
      if (!tabs.includes(type) && type !== "OTHERS") {
        tabs.push(type);
      }
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(item);
      return acc;
    }, {});
    return { tabs, categorizedFAQs };
  }, [QuestionAnswer]);
  const [activeTab, setActiveTab] = useState(tabs[0] || null);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(
    categorizedFAQs[tabs[0]]?.length > 0 ? 0 : null
  );
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (!tabs.includes(activeTab)) {
      setActiveTab(tabs[0] || null);
      setExpandedQuestion(categorizedFAQs[tabs[0]]?.length > 0 ? 0 : null);
    }
  }, [tabs, categorizedFAQs, activeTab]);

  const toggleQuestion = useCallback((index: number) => {
    setExpandedQuestion((prev) => (prev === index ? null : index));
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    startTransition(() => {
      setActiveTab(tab);
      setExpandedQuestion(categorizedFAQs[tab]?.length > 0 ? 0 : null);
    });
  }, [categorizedFAQs]);

  return (
    <section>
      <div className={cn("lg:col-span-8", styles.faqblock)}>
        <h2 id = "faq-heading"
          className={cn("mb-8", styles.mainHeadingFaq , isHome && styles.homeHeadingFaq  )}
        >
          Frequently Asked Questions
        </h2>
        {/* Tabs */}
        {tabs.length > 0 ? (
          <div className={cn("flex gap-2 mb-6 flex-wrap", styles.faqtab)}>
            {tabs.map((tab, index) => (
              <button
              id={`faq-button-${index + 1}`}
                disabled={activeTab === tab}
                key={tab}
                type="button"
                className={cn(
                  "px-2 py-2 sm:text-[11px] text-sm font-semibold rounded-lg",
                  styles.tabBtnFaq,
                  {
                    [styles.activeTabFaq]: activeTab === tab,
                    [styles.deactiveTabFaq]: activeTab !== tab,
                  }
                )}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No FAQs available.</p>
        )}
        {/* Questions */}
        {activeTab && categorizedFAQs[activeTab]?.length > 0 ? (
          <div
            key={activeTab}
            className={cn("md:px-0 sm:!pl-2 px-4 space-y-4", styles.tabPanel)}
          >
            {categorizedFAQs[activeTab].map((faq, index) => (
              <FaqItem
                key={index}
                faq={faq}
                index={index}
                isOpen={expandedQuestion === index}
                onToggle={toggleQuestion}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No FAQs available for this category.</p>
        )}
      </div>
    </section>
  );
};

export default FaqWithCategoryProgram;
